import { Capacitor } from '@capacitor/core';

export const APP_ANALYTICS_EVENTS = [
  'app_open',
  'session_start',
  'screen_view',
  'onboarding_complete',
  'auth_success',
  'auth_failure',
  'game_change',
  'smart_pick_generate',
  'smart_pick_error',
  'scan_start',
  'scan_success',
  'scan_failure',
  'manual_ticket_entry',
  'ticket_save',
  'ticket_delete',
  'ticket_result_refresh',
  'paywall_view',
  'purchase_start',
  'purchase_success',
  'purchase_failure',
  'notification_pref_change',
  'client_error',
  'unhandled_rejection',
  'api_error',
] as const;

export type AnalyticsEventName = (typeof APP_ANALYTICS_EVENTS)[number];

export interface AnalyticsEventInput {
  screen?: string;
  gameId?: string;
  source?: string;
  outcome?: string;
  value?: number;
  durationMs?: number;
  errorCode?: string;
  requestId?: string;
}

interface QueuedAnalyticsEvent extends AnalyticsEventInput {
  id: string;
  name: AnalyticsEventName;
  ts: string;
  platform: 'web' | 'ios' | 'android';
  appVersion?: string;
}

const INSTALL_ID_KEY = 'chancey_analytics_install_id';
const QUEUE_KEY = 'chancey_analytics_queue';
const MAX_QUEUE = 200;
const EVENT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const FLUSH_INTERVAL_MS = 30_000;

let initialized = false;
let flushing = false;
let queue: QueuedAnalyticsEvent[] = [];
let tokenProvider: (() => Promise<string | null>) | null = null;
let intervalId: number | null = null;

const sessionId = randomId();
const platform = normalizePlatform(Capacitor.getPlatform());
const appVersion = process.env.REACT_APP_VERSION || process.env.REACT_APP_BUILD_VERSION || 'unknown';

export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;
  queue = readQueue();
  track('app_open');
  track('session_start');
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushAnalytics();
  });
  window.addEventListener('pagehide', () => void flushAnalytics());
  window.addEventListener('error', (event) => {
    track('client_error', {
      source: 'window_error',
      errorCode: normalizeErrorCode(event.error instanceof Error ? event.error.name : event.message),
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    track('unhandled_rejection', {
      source: 'promise',
      errorCode: normalizeErrorCode(reasonName(event.reason)),
    });
  });
  intervalId = window.setInterval(() => void flushAnalytics(), FLUSH_INTERVAL_MS);
}

export function setAnalyticsTokenProvider(provider: (() => Promise<string | null>) | null): void {
  tokenProvider = provider;
}

export function shutdownAnalytics(): void {
  if (intervalId !== null) window.clearInterval(intervalId);
  intervalId = null;
  tokenProvider = null;
}

export function track(name: AnalyticsEventName, input: AnalyticsEventInput = {}): void {
  const event: QueuedAnalyticsEvent = {
    id: randomId(),
    name,
    ts: new Date().toISOString(),
    platform,
    appVersion,
    ...safeInput(input),
  };
  queue = [...queue.filter((queued) => !expired(queued)), event].slice(-MAX_QUEUE);
  writeQueue(queue);
  if (queue.length >= 10) void flushAnalytics();
}

export async function flushAnalytics(): Promise<void> {
  if (flushing || queue.length === 0) return;
  const base = process.env.REACT_APP_API_BASE_URL ?? '';
  if (!base) return;
  flushing = true;
  const batch = queue.slice(0, 50);
  try {
    const headers: Record<string, string> = { 'content-type': 'application/json' };
    const token = tokenProvider ? await tokenProvider() : null;
    if (token) headers.authorization = `Bearer ${token}`;
    const res = await fetch(`${base}/v1/analytics/events`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        installId: installId(),
        sessionId,
        events: batch,
      }),
      keepalive: true,
    });
    if (!res.ok) return;
    const sentIds = new Set(batch.map((event) => event.id));
    queue = queue.filter((event) => !sentIds.has(event.id));
    writeQueue(queue);
  } catch {
    /* keep queued for retry */
  } finally {
    flushing = false;
  }
}

function safeInput(input: AnalyticsEventInput): AnalyticsEventInput {
  return {
    ...(input.screen ? { screen: safeDimension(input.screen) } : {}),
    ...(input.gameId ? { gameId: safeDimension(input.gameId) } : {}),
    ...(input.source ? { source: safeDimension(input.source) } : {}),
    ...(input.outcome ? { outcome: safeDimension(input.outcome) } : {}),
    ...(typeof input.value === 'number' && Number.isFinite(input.value) ? { value: input.value } : {}),
    ...(typeof input.durationMs === 'number' && Number.isFinite(input.durationMs)
      ? { durationMs: Math.max(0, Math.min(600_000, input.durationMs)) }
      : {}),
    ...(input.errorCode ? { errorCode: safeDimension(input.errorCode) } : {}),
    ...(input.requestId ? { requestId: safeDimension(input.requestId) } : {}),
  };
}

function installId(): string {
  try {
    const existing = localStorage.getItem(INSTALL_ID_KEY);
    if (existing) return existing;
    const next = randomId();
    localStorage.setItem(INSTALL_ID_KEY, next);
    return next;
  } catch {
    return randomId();
  }
}

function readQueue(): QueuedAnalyticsEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isQueuedEvent).filter((event) => !expired(event)).slice(-MAX_QUEUE);
  } catch {
    return [];
  }
}

function writeQueue(next: QueuedAnalyticsEvent[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(next));
  } catch {
    /* storage can be full/private; memory queue still exists */
  }
}

function isQueuedEvent(value: unknown): value is QueuedAnalyticsEvent {
  if (!value || typeof value !== 'object') return false;
  const event = value as QueuedAnalyticsEvent;
  return (
    typeof event.id === 'string' &&
    typeof event.name === 'string' &&
    (APP_ANALYTICS_EVENTS as readonly string[]).includes(event.name) &&
    typeof event.ts === 'string' &&
    (event.platform === 'web' || event.platform === 'ios' || event.platform === 'android')
  );
}

function expired(event: QueuedAnalyticsEvent): boolean {
  const ts = Date.parse(event.ts);
  return !Number.isFinite(ts) || Date.now() - ts > EVENT_TTL_MS;
}

function randomId(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function normalizePlatform(value: string): 'web' | 'ios' | 'android' {
  if (value === 'ios') return 'ios';
  if (value === 'android') return 'android';
  return 'web';
}

function safeDimension(value: string): string {
  return value.trim().slice(0, 80).replace(/[^a-zA-Z0-9_./:-]/g, '_');
}

function normalizeErrorCode(value: string): string {
  return safeDimension(value || 'unknown').slice(0, 64);
}

function reasonName(reason: unknown): string {
  if (reason instanceof Error) return reason.name || reason.message;
  if (typeof reason === 'string') return reason;
  return 'unknown';
}
